import { IGeographicAddress } from '@features/eligibility/interfaces';
import { Icon } from '@features/siteStatusV2/components/icon';
import theme from '@theme';
import React, { useCallback, useState } from 'react';
import { Card, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { BuildingFill, ChevronDown, ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'src/app/store';
import styled from 'styled-components';

import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';
import AccordionToggle from './AccordionToggle';
import MapBadgeInformations from './constants/BadgeInformations.map';
import { IdentityHeaderSkelton } from './skeltons';

import 'react-loading-skeleton/dist/skeleton.css';

interface IdentityHeaderProps {
  addressInfos: IGeographicAddress | undefined;
  isAccordionOpen: boolean;
  accordionToggleRef: React.RefObject<HTMLButtonElement>;
}

const IdentityHeader: React.FC<IdentityHeaderProps> = ({
  addressInfos,
  isAccordionOpen,
  accordionToggleRef
}) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);
  const { isSiteLoading, site } = useSelector((state: RootState) => state.site);
  const [isCopyBtnActive, setIsCopyBtnActive] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleCopyButtonClick = useCallback(() => {
    copyBuildingInformations();
  }, [addressInfos, site]);

  const copyBuildingInformations = useCallback(async () => {
    const formatValue = (value: string | number | undefined): string =>
      value !== undefined && value !== null && value !== '' ? String(value).trim() : '-';

    const getAddress = () => {
      const parts = [
        addressInfos?.streetNr,
        site?.buildingStreetNumberComplement,
        addressInfos?.streetName,
        addressInfos?.postcode,
        addressInfos?.city
      ].filter(Boolean);
      return parts.length ? parts.join(' ') : '-';
    };

    const getPtoReferences = () => {
      return (
        site?.buildings
          ?.flatMap((building) =>
            building.stairs?.flatMap((stair) =>
              stair.floors?.flatMap((floor) =>
                floor.pto ? floor.pto.map((pto) => pto.ptoReference) : []
              )
            )
          )
          ?.join(', ') || '-'
      );
    };

    const eligibilityText = site?.eligibilityStatus
      ? t(MapBadgeInformations[site.eligibilityStatus]?.badgeText)
      : '-';

    const data: Record<string, string | number | undefined> = {
      [t('buildingDetail.buildingRef')]: addressInfos?.buildingRef,
      [t('buildingDetail.dsp')]: site?.dsp,
      [t('orderDetails.generalInformations.address')]: getAddress(),
      [t('buildingDetail.numberOfHousings')]: addressInfos?.housingUnitsNr,
      [t('buildingDetail.eligibilityState')]: eligibilityText,
      [t('buildingDetail.commercialOpenDate')]: site?.commercialOpeningDate,
      [t('buildingDetail.copperEndDate')]: site?.copperEndDate,
      [t('buildingDetail.connectionType')]: site?.connectionType,
      [t('buildingDetail.referencepm')]: site?.referencepm,
      [t('verticalityForm.ptoList', 'PTO list')]: getPtoReferences()
    };

    const formattedText = Object.entries(data)
      .map(([key, value]) => `${key}: ${formatValue(value)}`)
      .join('\n');

    try {
      await navigator.clipboard.writeText(formattedText);
      setIsCopyBtnActive(true);
      setShowTooltip(true);
      setTimeout(() => setShowTooltip(false), 2000);
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  }, [addressInfos, site]);

  return (
    <StyledCardHeader className="d-flex justify-content-between align-items-center px-4 py-3">
      <div className="d-flex align-items-center">
        {addressInfos ? (
          <AddressContent
            addressInfos={addressInfos}
            isSiteLoading={isSiteLoading}
            siteDsp={site?.dsp}
          />
        ) : (
          <IdentityHeaderSkelton.SkeletonContent />
        )}
      </div>
      <div className="d-flex align-items-center">
        <OverlayTrigger
          placement="top"
          show={showTooltip}
          overlay={<Tooltip id="copy-tooltip">{t('utils.tooltip.copied')}</Tooltip>}
        >
          <CopyButton
            onClick={handleCopyButtonClick}
            isActive={isCopyBtnActive}
            disabled={!addressInfos || !site}
          >
            <DuplicateIcon iconName="duplicate" />
          </CopyButton>
        </OverlayTrigger>
        <AccordionToggle accordionToggleRef={accordionToggleRef} eventKey="0" primary>
          <RotatableIcon
            isOpen={isAccordionOpen}
            width={13}
            height={13}
            fill={theme.palette.blue[500]}
          />
        </AccordionToggle>
      </div>
    </StyledCardHeader>
  );
};

const AddressContent: React.FC<{
  addressInfos: IGeographicAddress;
  isSiteLoading: boolean;
  siteDsp?: string;
}> = ({ addressInfos, isSiteLoading, siteDsp }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);

  return (
    <>
      <IconWrapper>
        <BuildingFill width={8} height={8} fill={theme.palette.blue[500]} />
      </IconWrapper>
      <CardTitle>{t('buildingDetail.buildingIdCard', `Building identity card`)}</CardTitle>
      <CardSectionTitle>
        <ChevronLeft width={8} height={8} fill={theme.palette.grey[500]} />
        <ChevronRight width={8} height={8} fill={theme.palette.grey[500]} />
        <span className="mx-1">{`ID: ${addressInfos.id}`}</span>
      </CardSectionTitle>
      {isSiteLoading ? (
        <IdentityHeaderSkelton.SkeletonDetails />
      ) : (
        siteDsp && <DspTag>{siteDsp}</DspTag>
      )}
    </>
  );
};

const StyledCardHeader = styled(Card.Header)`
  background-color: ${theme.palette.grey[50]} !important;
  border: none;
`;

const IconWrapper = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 100%;
  margin-right: 0.5rem;
  background-color: ${theme.palette.blue[100]};
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CardTitle = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.grey[900]};
  position: relative;
  padding-left: 0.5rem;
  padding-right: 1rem;

  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background: ${theme.palette.grey[400]};
    border-radius: 50%;
  }
`;

const CardSectionTitle = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.grey[400]};
  margin-left: 0.625rem;
  background: ${theme.palette.grey[100]};
  padding: 0.125rem 0.5rem;
  border-radius: 5px;
`;

const RotatableIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  transition: transform 0.3s;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;

const DspTag = styled.div`
  font-size: ${theme.fonts.size.xs};
  font-weight: ${theme.fonts.weight.medium};
  color: ${theme.palette.blue[600]};
  margin-left: 1.25rem;
  background: ${theme.palette.blue[100]};
  padding: 0.125rem 0.5rem;
  border-radius: 5px;
  position: relative;

  &::before {
    content: '';
    display: inline-block;
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 4px;
    background: ${theme.palette.grey[400]};
    border-radius: 50%;
  }
`;

const CopyButton = styled.button<{ isActive: boolean }>`
  display: flex;
  width: 1.5rem;
  height: 1.5rem;
  justify-content: center;
  align-items: center;
  background-color: ${({ isActive, theme }) =>
    isActive ? theme.palette.grey[300] : theme.palette.grey[100]};
  border-radius: 0.25rem;
  cursor: pointer;
  border: none;
  margin-right: 1rem;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${theme.palette.grey[300]};
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const DuplicateIcon = styled(Icon)`
  width: 1rem;
  color: ${theme.palette.grey[400]};
`;

export default IdentityHeader;
